﻿import { MediaService } from "../../services/media.service.js";
import { WebtoonAutoSave } from "./WebtoonAutoSave.js";
import { getFirestore, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { app } from "../../firebase-config.js";

const db = getFirestore(app);

/**
 * [Webtoon Episode Editor]
 * Handles Image List, Staging, Uploading, and Publishing.
 * Refactored from WebtoonEditor.js.
 */
export class WebtoonEpisodeEditor {
    constructor(container, controller, episodeIndex) {
        this.container = container;
        this.controller = controller;
        this.index = episodeIndex;
        this.episode = controller.episodes[episodeIndex];

        // [FIX] Isolate File State per Episode
        // Previously: this.files = controller.files (Shared Global State) -> Caused "18 documents in Ep 1" bug
        // Now: Local state initialized from existing links
        this.localFiles = (this.episode.links || []).map(url => ({
            id: crypto.randomUUID(), // Generate ID for existing
            url: url,
            name: 'Existing Image',
            isLocal: false,
            status: 'done'
        }));

        this.uploadPromise = null; // [Concurrency] Track upload state

        this.sortable = null;
        this.autoSaveCleanup = null;
    }

    // [Lifecycle] Cleanup to prevent zombie AutoSaves
    destroy() {
        if (this.autoSaveCleanup) {
            this.autoSaveCleanup.cleanup();
            this.autoSaveCleanup = null;
        }
    }

    render() {
        // ... (render logic) ...
        const ep = this.episode;
        const isDraft = ep.status === 'draft' || !ep.status;

        this.container.innerHTML = `
        <div class="w-full h-full flex flex-col bg-zinc-900 text-white font-sans relative overflow-hidden" style="background-image: url('/login.webp'); background-size: cover; background-position: center;">
            <div class="absolute inset-0 bg-zinc-900/90 backdrop-blur-md z-0"></div>

            <div class="relative z-10 w-full h-full flex flex-row max-w-[1920px] mx-auto shadow-2xl overflow-hidden">
                
                <!-- Left: Editor Inputs (Fixed Width) -->
                <aside class="w-[450px] min-w-[450px] flex flex-col border-r border-white/10 bg-black/80 h-full shadow-xl z-20 flex-shrink-0 backdrop-blur-md">
                    <!-- Header -->
                    <div class="p-4 border-b border-white/10 flex items-center justify-between bg-black/50 flex-shrink-0">
                        <div class="flex items-center gap-3">
                            <button id="btn-back-hub" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
                                <span class="material-symbols-outlined">arrow_back</span>
                            </button>
                            <div class="flex flex-col">
                                <span class="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">${window.locale.t('editor.webtoon_editor')}</span>
                                <span class="font-bold text-white leading-none">${window.locale.t('editor.episode_prefix') || 'Episode'} ${this.index + 1}</span>
                            </div>
                        </div>
                        
                        <!-- Status Badge -->
                         <div class="px-2 py-1 rounded text-[10px] font-bold border flex items-center gap-1
                            ${isDraft
                ? 'bg-zinc-800 text-zinc-400 border-zinc-700'
                : 'bg-indigo-900/50 text-indigo-300 border-indigo-500/30'
            }">
                            <span class="material-symbols-outlined text-[12px]">${isDraft ? 'lock' : 'rocket_launch'}</span>
                            ${isDraft ? (window.locale.t('common.draft') || 'DRAFT') : (window.locale.t('common.published') || 'PUBLISHED')}
                        </div>
                        
                        <!-- AI Analysis Button (Admin Only) -->
                        ${(window.user && (window.user.role === 'admin' || window.user.isSuperAdmin)) ? `
                        <button id="btn-ai-analyze" class="ml-2 w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500 hover:text-white transition-colors border border-indigo-500/30" title="${window.locale.t('editor.ai_analyze_admin')}">
                            <span class="material-symbols-outlined text-sm">smart_toy</span>
                        </button>
                        ` : ''}
                    </div>

                    <!-- Scrollable Form Area -->
                    <div class="flex-grow overflow-y-auto p-5 space-y-6 custom-scrollbar" id="editor-scroll-area">
                        <!-- Visual Draft Warning (If Draft) -->
                        ${isDraft ? `
                        <div class="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg flex items-center gap-3 animate-fade-in relative overflow-hidden group">
                             <div class="absolute inset-0 bg-amber-500/5 group-hover:bg-amber-500/10 transition-colors"></div>
                             <span class="material-symbols-outlined text-amber-500 relative z-10">lock</span>
                             <div class="relative z-10">
                                <h4 class="text-sm font-bold text-amber-500">${window.locale.t('editor.draft_mode_title')}</h4>
                                <p class="text-xs text-zinc-400">${window.locale.t('editor.draft_mode_private_desc')}</p>
                             </div>
                        </div>
                        ` : ''}

                        <input id="ep-title-input" class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-white focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all placeholder-zinc-600" value="${ep.title || ''}" placeholder="${window.locale.t('editor.input_title_placeholder')}">
                    

                        <!-- Display Mode Selection (Radio) -->
                        <div class="space-y-2">
                            <label class="text-xs font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1">
                                <span class="material-symbols-outlined text-sm">view_agenda</span> ${window.locale.t('editor.display_mode')}
                            </label>
                            <div class="grid grid-cols-3 gap-2">
                                <!-- Webtoon -->
                                <label class="cursor-pointer group relative">
                                    <input type="radio" name="ep-display-mode" value="webtoon" class="peer hidden" ${(!ep.displayMode || ep.displayMode === 'webtoon') ? 'checked' : ''}>
                                    <div class="bg-zinc-800 border border-white/10 peer-checked:border-indigo-500 peer-checked:bg-indigo-500/10 rounded-xl p-3 flex flex-col items-center gap-1 transition-all hover:bg-zinc-700">
                                        <span class="material-symbols-outlined text-2xl text-zinc-500 peer-checked:text-indigo-400">crop_portrait</span>
                                        <span class="text-[10px] font-bold text-zinc-400 peer-checked:text-white">${window.locale.t('editor.mode_webtoon')}</span>
                                    </div>
                                </label>
                                <!-- Comics -->
                                <label class="cursor-pointer group relative">
                                    <input type="radio" name="ep-display-mode" value="comic" class="peer hidden" ${(ep.displayMode === 'comic') ? 'checked' : ''}>
                                    <div class="bg-zinc-800 border border-white/10 peer-checked:border-indigo-500 peer-checked:bg-indigo-500/10 rounded-xl p-3 flex flex-col items-center gap-1 transition-all hover:bg-zinc-700">
                                        <span class="material-symbols-outlined text-2xl text-zinc-500 peer-checked:text-indigo-400">auto_stories</span>
                                        <span class="text-[10px] font-bold text-zinc-400 peer-checked:text-white">${window.locale.t('editor.mode_comic')}</span>
                                    </div>
                                </label>
                                <!-- InstaToon -->
                                <label class="cursor-pointer group relative">
                                    <input type="radio" name="ep-display-mode" value="insta" class="peer hidden" ${(ep.displayMode === 'insta') ? 'checked' : ''}>
                                    <div class="bg-zinc-800 border border-white/10 peer-checked:border-indigo-500 peer-checked:bg-indigo-500/10 rounded-xl p-3 flex flex-col items-center gap-1 transition-all hover:bg-zinc-700">
                                        <span class="material-symbols-outlined text-2xl text-zinc-500 peer-checked:text-indigo-400">burst_mode</span>
                                        <span class="text-[10px] font-bold text-zinc-400 peer-checked:text-white">${window.locale.t('editor.mode_insta')}</span>
                                    </div>
                                </label>
                            </div>
                            <p class="text-[10px] text-zinc-500">${window.locale.t('editor.mode_desc')}</p>
                        </div>

                        <!-- Episode Thumbnail -->
                        <div class="space-y-2">
                            <label class="text-xs font-bold text-indigo-300 uppercase tracking-wider">${window.locale.t('editor.thumbnail')}</label>
                            <div class="flex items-center gap-3">
                                 <div class="w-16 h-16 bg-zinc-800 rounded-lg overflow-hidden border border-white/10 flex-shrink-0 relative group">
                                    <img id="ep-thumb-preview" src="${ep.thumb || ''}" class="w-full h-full object-cover ${ep.thumb ? '' : 'hidden'}">
                                    <div class="${ep.thumb ? 'hidden' : ''} w-full h-full flex items-center justify-center text-zinc-600">
                                        <span class="material-symbols-outlined text-xl">image</span>
                                    </div>
                                </div>
                                <div class="flex-grow space-y-2">
                                    <input class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-2.5 text-xs text-zinc-300 font-mono focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all" id="ep-thumb-input" value="${ep.thumb || ''}" placeholder="Thumbnail URL">
                                    <label class="cursor-pointer inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-white transition-colors text-xs font-bold">
                                        <span class="material-symbols-outlined text-sm">upload</span> ${window.locale.t('editor.upload_image')}
                                        <input type="file" id="ep-thumb-upload" class="hidden" accept="image/*">
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- BGM Input Section (Simplified) -->
                        <div class="space-y-2">
                            <label class="text-xs font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1">
                                <span class="material-symbols-outlined text-sm">music_note</span> ${window.locale.t('editor.bgm')}
                            </label>
                            <input id="ep-bgm-input" class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-sm text-white focus:border-indigo-500 focus:bg-zinc-800 outline-none transition-all placeholder-zinc-600" value="${ep.bgmUrl || ''}" placeholder="Suno AI or MP3 Link">
                        </div>

                        <!-- Image Upload Section -->
                        <div class="space-y-3">
                            <div class="flex justify-between items-end">
                                <label class="text-xs font-bold text-indigo-300 uppercase tracking-wider">${window.locale.t('editor.manuscript_images')}</label>
                                <span id="file-count" class="text-xs text-indigo-400 font-mono bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">0 files</span>
                            </div>

                            <label id="drag-drop-area" class="cursor-pointer group block relative overflow-hidden transition-all duration-200">
                                <input type="file" id="file-upload-input" multiple accept="image/*" class="hidden">
                                <div id="drag-drop-visual" class="border-2 border-dashed border-zinc-700 group-hover:border-indigo-500 bg-zinc-800/30 group-hover:bg-zinc-800/50 rounded-xl p-8 text-center transition-all duration-300 pointer-events-none">
                                    <div class="mb-3 w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform shadow-lg group-hover:shadow-indigo-500/20">
                                        <span class="material-symbols-outlined text-2xl text-zinc-500 group-hover:text-indigo-400">add_photo_alternate</span>
                                    </div>
                                    <p class="text-sm font-bold text-zinc-300 group-hover:text-white">${window.locale.t('editor.drag_drop_hint')}</p>
                                </div>
                            </label>

                            <div id="staging-list" class="space-y-2 min-h-[100px] p-1 bg-black/20 rounded-xl border border-white/5"></div>
                        </div>
                    </div>

                    <!-- Footer Actions (Draft / Save / Publish) -->
                    <div class="p-4 border-t border-white/10 bg-black/50 space-y-3 z-30 backdrop-blur-md">
                         <!-- Progress Bar -->
                        <div id="progress-container" class="hidden space-y-2">
                            <div class="flex justify-between text-xs text-zinc-400 font-mono">
                                <span id="progress-status">${window.locale.t('editor.processing')}</span>
                                <span id="progress-percent">0%</span>
                            </div>
                            <div class="w-full bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                                <div id="progress-bar" class="bg-indigo-500 h-full w-0 transition-all duration-300"></div>
                            </div>
                        </div>

                        <div class="grid grid-cols-4 gap-2">
                            <!-- Draft -->
                            <button id="btn-draft" class="col-span-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-amber-500 font-bold rounded-xl text-sm transition-colors border border-amber-500/20 flex items-center justify-center gap-2" title="${window.locale.t('editor.set_draft')}">
                                <span class="material-symbols-outlined text-sm">lock</span> ${window.locale.t('editor.set_draft')}
                            </button>

                            <!-- Publish/Save -->
                            <button id="btn-publish" class="col-span-2 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl shadow-lg shadow-indigo-900/40 flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
                                <span class="material-symbols-outlined">${isDraft ? 'rocket_launch' : 'save'}</span> 
                                ${isDraft ? (window.locale.t('editor.publish')) : (window.locale.t('editor.save_changes'))}
                            </button>
                        </div>
                    </div>
                </aside>

                <!-- Right: Preview -->
                <main class="flex-1 min-w-0 bg-black relative h-full flex flex-col border-l border-white/10 overflow-hidden">
                    <div class="h-14 bg-zinc-900 border-b border-white/10 flex items-center justify-between px-6 shadow-sm flex-shrink-0 z-10">
                        <span class="text-xs font-mono text-zinc-400 bg-black/40 px-3 py-1 rounded-full border border-white/5 flex items-center gap-2">
                            <div class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div> ${window.locale.t('editor.live_preview')}
                        </span>
                        <button id="btn-refresh-preview" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-zinc-500 hover:text-white transition-colors">
                            <span class="material-symbols-outlined text-sm">refresh</span>
                        </button>
                    </div>
                    <div class="flex-grow relative w-full h-full bg-zinc-950 overflow-y-auto custom-scrollbar flex justify-center" id="preview-container">
                        <div class="flex flex-col items-center justify-center h-full text-zinc-600 gap-3 opacity-50 select-none">
                            <span class="material-symbols-outlined text-5xl">smartphone</span>
                            <span class="text-sm font-medium">${window.locale.t('editor.preview_hint')}</span>
                        </div>
                    </div>
                </main>
            </div>
        </div>
        `;

        this.bindEvents();
        this.renderStagingList();

        // AutoSave Init
        const scrollArea = this.container.querySelector('#editor-scroll-area');
        if (this.autoSaveCleanup) this.autoSaveCleanup.cleanup();

        // Listener uses the wrapper object returned by init
        const autoSave = WebtoonAutoSave.init(
            () => this.saveToDB(this.episode.status || 'draft', true), // Silent save
            scrollArea // Listener attached to form area
        );
        this.autoSaveCleanup = autoSave;

        // Expose trigger to Sortable
        this.triggerAutoSave = autoSave.trigger;
    }

    bindEvents() {
        this.container.querySelector('#btn-back-hub').onclick = () => {
            if (this.autoSaveCleanup) this.autoSaveCleanup.cleanup();
            this.controller.currentEditorIndex = -1;
            this.controller.render();
        };

        // Helper: Process File List
        const processFiles = (filesInput) => {
            const newFiles = Array.from(filesInput);
            if (!newFiles.length) return;

            // [Validation Logic] Check Mode
            const currentMode = this.container.querySelector('input[name="ep-display-mode"]:checked')?.value || 'webtoon';

            newFiles.forEach(f => {
                if (!f.type.startsWith('image/')) return;

                // [Strict Check] Async Image Load to check dimensions
                const img = new Image();
                img.onload = () => {
                    const validation = this.validateImage(img.width, img.height, currentMode);
                    if (!validation.valid) {
                        window.Modal.alert(
                            window.locale.t('editor.image_mode_mismatch', { mode: currentMode.toUpperCase() }),
                            window.locale.t('editor.invalid_format')
                        );
                        return; // Reject
                    }

                    // Valid -> Add to List
                    this.localFiles.push({
                        // [UUID Migration] File IDs should be unique too, but for legacy compatibility we use random string
                        id: crypto.randomUUID(),
                        file: f,
                        url: null,
                        name: f.name,
                        size: f.size,
                        isLocal: true,
                        status: 'pending'
                    });
                    this.renderStagingList();
                    this.triggerAutoSave();
                };
                img.src = URL.createObjectURL(f);
            });
        };

        // Mode Switch Block Logic
        const modeRadios = this.container.querySelectorAll('input[name="ep-display-mode"]');
        let lastMode = this.episode.displayMode || 'webtoon'; // Track last valid mode to revert

        modeRadios.forEach(radio => {
            radio.addEventListener('change', async (e) => {
                const newMode = e.target.value;

                // [STRICT VALIDATION] Check ALL existing files against new mode
                if (this.localFiles.length > 0) {
                    const invalidFiles = [];

                    // Show loading if many files? (Usually fast enough)

                    const checks = this.localFiles.map(fileItem => {
                        return new Promise((resolve) => {
                            const img = new Image();
                            img.onload = () => {
                                const valid = this.validateImage(img.width, img.height, newMode);
                                resolve({ name: fileItem.name, ...valid });
                            }
                            img.onerror = () => resolve({ name: fileItem.name, valid: true }); // Skip error files

                            // Use ObjectURL for local, URL for remote
                            if (fileItem.isLocal && fileItem.file) {
                                img.src = URL.createObjectURL(fileItem.file);
                            } else {
                                img.src = fileItem.url; // Might benefit from CORS/Caching, but simple src usually works for dimensions if cached
                            }
                        });
                    });

                    const results = await Promise.all(checks);

                    // Filter invalid results
                    results.forEach(res => {
                        if (!res.valid) invalidFiles.push(res);
                    });

                    if (invalidFiles.length > 0) {
                        // [BLOCK SWITCH] & [UI REVERT]
                        e.preventDefault();
                        // Revert Radio Selection
                        e.target.checked = false;
                        const lastRadio = this.container.querySelector(`input[name="ep-display-mode"][value="${lastMode}"]`);
                        if (lastRadio) lastRadio.checked = true;

                        window.Modal.alert(
                            window.locale.t('editor.mode_change_blocked_desc', { mode: newMode.toUpperCase() }),
                            window.locale.t('editor.mode_change_blocked')
                        );
                        return;
                    }
                }

                // Passed Validation
                lastMode = newMode;
                this.episode.displayMode = newMode; // Update local state immediately
                this.updatePreviewDOM(); // Refresh Preview Layout immediately
                this.triggerAutoSave();
            });
        });

        // File Input (Click)
        const fileInput = this.container.querySelector('#file-upload-input');
        if (fileInput) {
            fileInput.onchange = (e) => {
                processFiles(e.target.files);
                fileInput.value = '';
            };
        }

        // Drag and Drop Logic
        const dropArea = this.container.querySelector('#drag-drop-area');
        const dropVisual = this.container.querySelector('#drag-drop-visual');

        if (dropArea && dropVisual) {
            ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
                dropArea.addEventListener(eventName, (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }, false);
            });

            // Highlight on drag
            ['dragenter', 'dragover'].forEach(eventName => {
                dropArea.addEventListener(eventName, () => {
                    dropVisual.classList.add('border-indigo-500', 'bg-indigo-500/10');
                }, false);
            });

            // Remove highlight
            ['dragleave', 'drop'].forEach(eventName => {
                dropArea.addEventListener(eventName, () => {
                    dropVisual.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                }, false);
            });

            // Handle Drop
            dropArea.addEventListener('drop', (e) => {
                const dt = e.dataTransfer;
                processFiles(dt.files);
            }, false);
        }

        // Thumb Upload
        const thumbUpload = this.container.querySelector('#ep-thumb-upload');
        if (thumbUpload) {
            thumbUpload.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                // ... (Reuse upload logic from before, but simplified) ...
                // For brevity, assuming MediaService handles it
                try {
                    const compressed = await MediaService.compressImage(file);
                    const url = await MediaService.uploadImage(compressed, `series/${this.controller.seriesId}/thumbs/${Date.now()}.webp`);
                    this.container.querySelector('#ep-thumb-input').value = url;
                    this.container.querySelector('#ep-thumb-preview').src = url;
                    this.container.querySelector('#ep-thumb-preview').classList.remove('hidden');
                    this.triggerAutoSave();
                } catch (e) { console.error(e); }
            };
        }

        // Buttons
        this.container.querySelector('#btn-draft').onclick = () => {
            // [UX] Explicit feedback for Draft Mode
            this.saveToDB('draft').then(() => {
                this.render(); // Re-render to show Badge
                window.Modal.alert(
                    `<div class="text-center">
                        <span class="material-symbols-outlined text-4xl text-amber-500 mb-2">lock</span>
                        <h3 class="font-bold text-white mb-1">${window.locale.t('editor.draft_mode_switched')}</h3>
                        <p class="text-sm text-zinc-400">${window.locale.t('editor.draft_mode_private_desc')}</p>
                      </div>`,
                    window.locale.t('common.draft') || "Draft Mode"
                );
            });
        };
        this.container.querySelector('#btn-publish').onclick = () => this.saveToDB('published');
        // Preview button removed
        // this.container.querySelector('#btn-preview').onclick ...
        this.container.querySelector('#btn-refresh-preview').onclick = () => this.renderStagingList();

        // Sortable Init
        import('sortablejs').then(module => {
            const el = this.container.querySelector('#staging-list');
            if (el) {
                new module.default(el, {
                    animation: 150,
                    handle: '.drag-handle',
                    ghostClass: 'bg-indigo-900/30',
                    onEnd: () => {
                        // Reorder global files logic
                        const itemIds = Array.from(el.children).map(c => c.dataset.id);
                        const newOrder = [];
                        itemIds.forEach(id => {
                            const item = this.localFiles.find(f => f.id === id);
                            if (item) newOrder.push(item);
                        });
                        this.localFiles = newOrder;
                        this.triggerAutoSave();
                        // [FIX] Update Preview Immediately after Reorder
                        this.updatePreviewDOM();
                    }
                });
            }
        });

        // AI Analysis Handler
        const btnAI = this.container.querySelector('#btn-ai-analyze');
        if (btnAI) {
            btnAI.onclick = async () => {
                const updatedEpId = this.episode.id || this.controller.episodes[this.index]?.id;

                if (!updatedEpId) {
                    window.Modal.alert(window.locale.t('editor.save_required'));
                    return;
                }

                if (!await window.Modal.confirm(window.locale.t('editor.ai_analyze_confirm'), window.locale.t('editor.ai_analyze_title'))) return;

                try {
                    btnAI.classList.add('animate-spin');

                    // [FIX] Valid Sequence: Save -> Trigger
                    // We MUST save first to ensure the backend sees the files we just uploaded or reordered.
                    await this.saveToDB(this.episode.status || 'draft', true); // Silent save

                    // [Step 1] Request Start
                    window.Modal.toast(window.locale.t('editor.ai_requesting'), window.locale.t('editor.ai_processing_title'));

                    const resData = await this.triggerAIAnalysis(updatedEpId);

                    // [Step 2] Result Processing
                    if (resData.processed === 0 && resData.debugLogs && resData.debugLogs.length > 0) {
                        const logMsg = resData.debugLogs.join('\n');
                        window.Modal.alert(window.locale.t('editor.ai_no_items', { log: logMsg }), window.locale.t('editor.diagnosis_report'));
                    } else {
                        // [Step 3] Success & SEO Trigger Feedback
                        window.Modal.toast(window.locale.t('editor.ocr_complete', { n: resData.successCount }), window.locale.t('common.success') || "Success");
                        setTimeout(() => {
                            window.Modal.toast(window.locale.t('editor.seo_trigger'), window.locale.t('common.system') || "System", 3000);
                        }, 1000);

                        window.Modal.alert(window.locale.t('editor.ai_complete_desc'));
                    }
                } catch (e) {
                    console.error("AI Error:", e);
                    window.Modal.toast(window.locale.t('editor.ai_error'), window.locale.t('common.error') || "Error");
                    window.Modal.alert(window.locale.t('editor.ai_fail_msg', { e: e.message }));
                } finally {
                    btnAI.classList.remove('animate-spin');
                }
            };
        }
    }

    // [New Helper] Helper for AI Trigger
    async triggerAIAnalysis(docId) {
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const { app } = await import("../../firebase-config.js");
        const functions = getFunctions(app);
        const triggerOCR = httpsCallable(functions, 'manuallyTriggerOCR');

        // Note: SeriesID is available in controller
        // [Compatibility] 'webtoon' mode works for 'comic' display mode too as file structure is same.
        const result = await triggerOCR({
            mode: 'webtoon',
            seriesId: this.controller.seriesId,
            episodeId: this.index,
            episodeDocId: docId,
            uid: window.user.uid
        });

        return result.data;
    }

    renderStagingList() {
        const listEl = this.container.querySelector('#staging-list');
        if (!listEl) return;
        listEl.innerHTML = '';

        this.localFiles.forEach(item => {
            const div = document.createElement('div');
            div.className = "flex items-center gap-3 bg-zinc-800 border border-zinc-700 p-2 rounded-lg group hover:border-zinc-600 transition-colors";
            div.dataset.id = item.id;

            // Thumbnail
            let thumb = item.isLocal && item.file ? URL.createObjectURL(item.file) : item.url;

            div.innerHTML = `
                <div class="drag-handle cursor-grab text-zinc-600 hover:text-zinc-400 p-1">
                    <span class="material-symbols-outlined">drag_indicator</span>
                </div>
                 <div class="w-10 h-10 bg-zinc-900 rounded overflow-hidden flex-shrink-0 border border-zinc-700">
                    <img src="${thumb}" class="w-full h-full object-cover">
                </div>
                <div class="flex-grow min-w-0">
                    <p class="text-xs font-bold text-zinc-300 truncate">${item.name}</p>
                </div>
                <button class="btn-remove p-1.5 text-zinc-500 hover:text-red-500 rounded transition-colors">
                    <span class="material-symbols-outlined text-sm">delete</span>
                </button>
             `;

            div.querySelector('.btn-remove').onclick = () => {
                this.localFiles = this.localFiles.filter(f => f.id !== item.id);
                this.renderStagingList();
                this.triggerAutoSave();
            };
            listEl.appendChild(div);
        });

        // Update Preview
        this.updatePreviewDOM();
    }

    updatePreviewDOM() {
        const pContainer = this.container.querySelector('#preview-container');
        if (!pContainer) return;

        // [Logic] Get current display mode
        const mode = this.episode.displayMode || 'webtoon';

        if (this.localFiles.length === 0) {
            pContainer.innerHTML = `<div class="flex flex-col items-center justify-center h-full text-zinc-600 gap-3 opacity-50 select-none"><span class="material-symbols-outlined text-5xl">smartphone</span><span class="text-sm font-medium">Add images to see preview</span></div>`;
            return;
        }

        // [Logic] Define Container Classes based on Mode
        let containerClass = "";
        let wrapperClass = "";
        let imgClass = "";

        switch (mode) {
            case 'webtoon':
                // Vertical Stack (Default)
                containerClass = "w-full max-w-[480px] bg-white min-h-full flex flex-col shadow-2xl";
                wrapperClass = "w-full";
                imgClass = "w-full block";
                pContainer.classList.remove('overflow-x-auto', 'items-center', 'justify-start'); // Reset
                pContainer.classList.add('overflow-y-auto', 'justify-center');
                break;

            case 'comic':
            case 'insta':
                // Horizontal Snap
                containerClass = "flex flex-row h-full w-full items-center"; // Inner container for horizontal
                wrapperClass = "h-full w-full flex-shrink-0 snap-center flex items-center justify-center";
                imgClass = "h-full w-auto object-contain max-w-full";

                if (mode === 'insta') {
                    wrapperClass += " p-4";
                    imgClass = "aspect-square object-contain max-h-full max-w-full shadow-2xl block bg-white";
                }

                // Outer container adjustments for Horizontal Scroll
                pContainer.classList.remove('overflow-y-auto', 'justify-center');
                pContainer.classList.add('overflow-x-auto', 'snap-x', 'snap-mandatory', 'items-center', 'justify-start');

                // [FEATURE] Horizontal Wheel Support
                pContainer.onwheel = (e) => {
                    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                        e.preventDefault();
                        pContainer.scrollBy({ left: e.deltaY, behavior: 'auto' });
                    }
                };

                // [FEATURE] Tap-to-Next Navigation
                pContainer.onclick = (e) => {
                    const width = pContainer.clientWidth;
                    const x = e.clientX - pContainer.getBoundingClientRect().left;

                    if (x < width * 0.3) {
                        // Left 30% -> Prev
                        pContainer.scrollBy({ left: -width, behavior: 'smooth' });
                    } else if (x > width * 0.7) {
                        // Right 30% -> Next
                        pContainer.scrollBy({ left: width, behavior: 'smooth' });
                    }
                };
                break;

            default:
                // Default Fallback
                pContainer.classList.remove('overflow-x-auto', 'items-center');
                pContainer.classList.add('overflow-y-auto', 'justify-center');
                containerClass = "w-full max-w-[480px] bg-white min-h-full flex flex-col shadow-2xl";
                wrapperClass = "w-full";
                imgClass = "w-full block";
        }

        // [Render]
        pContainer.innerHTML = `<div class="${containerClass}">
            ${this.localFiles.map((f, idx) => {
            const src = f.isLocal && f.file ? URL.createObjectURL(f.file) : f.url;

            return `
                <div class="${wrapperClass} relative">
                    <img src="${src}" class="${imgClass}" loading="lazy" draggable="false">
                    ${(mode === 'comic' || mode === 'insta') ? `<div class="absolute bottom-2 right-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded backdrop-blur-sm">${idx + 1} / ${this.localFiles.length}</div>` : ''}
                </div>`;
        }).join('')}
        </div>
        
        ${(mode === 'comic' || mode === 'insta') ?
                // Add Overlay Indicator for Horizontal Modes
                `<div class="fixed bottom-10 left-[calc(50%+225px)] -translate-x-1/2 bg-black/50 text-white text-[10px] px-3 py-1.5 rounded-full pointer-events-none backdrop-blur-sm border border-white/10 z-50 flex items-center gap-2 animate-fade-in shadow-xl">
                    <span class="material-symbols-outlined text-[14px] animate-pulse">touch_app</span>
                    ${window.locale.t('editor.tap_nav_hint')}
                </div>`
                : ''}
        `;
    }

    async saveToDB(status, silent = false) {
        if (!silent) {
            // Show Spinner (optional, handled by button UI usually)
        }

        // [FIX] Ensure UUID for Upload Path
        if (!this.episode.id) {
            this.episode.id = crypto.randomUUID();
            console.log("[WebtoonEpisodeEditor] Generated new UUID:", this.episode.id);
        }

        // 1. Gather Data
        const title = this.container.querySelector('#ep-title-input').value;
        const thumb = this.container.querySelector('#ep-thumb-input').value;
        const bgmUrl = this.container.querySelector('#ep-bgm-input').value;

        // [UI] Get Display Mode
        const displayMode = this.container.querySelector('input[name="ep-display-mode"]:checked')?.value || 'webtoon';

        // 2. Upload Pending Files
        // [FIX] Loop until NO pending files remain.
        // A single await only catches the *current* batch. 
        // If files are added *during* the upload, they would be skipped.
        if (!silent) window.Modal.toast("Verifying uploads...", "Saving");

        // Safety Break counter to prevent infinite loops (though unlikely)
        let loopCount = 0;
        while (this.localFiles.some(f => f.status === 'pending') && loopCount < 10) {
            if (!silent) window.Modal.toast(`Uploading batch ${loopCount + 1}...`, "Saving");
            await this.uploadPendingImages();
            loopCount++;
        }

        // 3. Prepare Data
        // Filter out items without URL (failed uploads)
        // [DEBUG] Log file states before saving
        console.log("[SaveToDB] Local Files Summary:", this.localFiles.map(f => ({
            name: f.name,
            status: f.status,
            hasUrl: !!f.url,
            url: f.url
        })));

        const readyFiles = this.localFiles.filter(f => f.url).map(f => f.url);

        console.log("[SaveToDB] Final Ready Links (DB Payload):", readyFiles);

        if (readyFiles.length === 0 && this.localFiles.length > 0) {
            console.error("[SaveToDB] CRITICAL: Local files exist but No URLs generated!", this.localFiles);
            if (!silent) window.Modal.alert(window.locale.t('editor.upload_fail_desc'));
            return; // Stop saving if critical failure
        }

        // [UX] Confirm URL Generation
        if (!silent && readyFiles.length > 0) {
            window.Modal.toast(`URL 생성 완료: ${readyFiles.length}개`, "진행 중");
        }

        // [UUID Migration] Use ID from `this.episode.id` (which is now UUID)
        const docId = this.episode.id || crypto.randomUUID();

        const data = {
            id: docId,
            title: title,
            thumb: thumb,
            bgmUrl: bgmUrl,
            status: status,
            links: readyFiles,
            updatedAt: new Date(),
            displayMode: displayMode,
            order: this.episode.order ?? 0 // Preserve order
        };

        try {
            // [GATE LOGIC START] - Admin Approval System
            let finalStatus = status;
            let isApprovalRequest = false;

            if (status === 'published') {
                const currentUser = window.user || {};
                const isAdmin = currentUser.role === 'admin' || currentUser.isSuperAdmin;

                // 1. Check strict server status for Series
                let isSeriesLive = false;
                try {
                    const snap = await getDoc(doc(db, "series", this.controller.seriesId));
                    if (snap.exists()) {
                        const serverData = snap.data();
                        // "Lifetime Pass": Check if currently published OR has been published before (fast-track)
                        // If 'publishedAt' exists, it means it was once approved.
                        if (serverData.status === 'published' || serverData.publishedAt) {
                            isSeriesLive = true;
                        }
                    }
                } catch (err) {
                    console.error("Gate Check Failed:", err);
                    // Fail safe: If check fails, assume NOT live (restrictive) unless Admin
                }

                if (!isSeriesLive && !isAdmin) {
                    // GATE CLOSED
                    console.log("[Webtoon] First Publication Gate: Enforcing Pending Status.");
                    finalStatus = 'pending';
                    isApprovalRequest = true;

                    // Ensure Series identifies as Pending
                    try {
                        await setDoc(doc(db, "series", this.controller.seriesId), {
                            status: 'pending',
                            updatedAt: serverTimestamp()
                        }, { merge: true });
                    } catch (e) { console.error("Failed to update series pending status", e); }
                } else if (!isSeriesLive && isAdmin) {
                    // [FIX] Admin Bypass: Ensure Series is ALSO Published
                    // Otherwise we get "Ghost State" (Episode Published, Series Draft)
                    console.log("[Webtoon] Admin Bypass: Publisher Gate Open. Syncing Series Status.");
                    try {
                        await setDoc(doc(db, "series", this.controller.seriesId), {
                            status: 'published',
                            publishedAt: serverTimestamp(),
                            updatedAt: serverTimestamp()
                        }, { merge: true });
                    } catch (e) { console.error("Failed to sync series published status", e); }
                }
            }

            // Apply Final Status
            data.status = finalStatus;
            // [GATE LOGIC END]

            // Delegate saving to Controller (which handles Firestore)
            // Note: We pass INDEX, not ID. Controller handles ID resolution via data.id
            // [FIX] Pass silent flag to prevent duplicate toasts
            await this.controller.saveEpisode(this.index, data, silent);

            // Update Local State
            this.episode.id = docId; // [FIX] Prevent Duplication on subsequent saves
            this.episode.title = title;
            this.episode.thumb = thumb;
            this.episode.status = finalStatus; // Use Final Status
            this.episode.links = readyFiles;
            this.episode.displayMode = displayMode;
            this.episode.bgmUrl = bgmUrl;

            // Re-render to reflect status change (Badge etc)
            // [UX FIX] Silent Save should NOT re-render the whole page.
            // It causes focus loss and scroll jumps.
            if (!silent) this.render();

            if (!silent) {
                if (isApprovalRequest) {
                    window.Modal.showApprovalRequestSuccess();
                } else if (status === 'draft') {
                    // Handled by btn click handler usually, but autosave might trigger logic
                    // If visual feedback needed for draft autosave? No, silent.
                    window.Modal.toast(window.locale.t('editor.saved_to_draft'), window.locale.t('common.success') || "Success");
                } else {
                    // [AUTO-TRIGGER] SEO Analysis on Publish
                    window.Modal.toast(window.locale.t('editor.published_start_ai'), window.locale.t('common.published') || "Published");

                    // Call Trigger non-blocking (Fire & Forget, or show progress?)
                    // Better to show it's happening.
                    this.triggerAIAnalysis(docId).catch(err => {
                        console.error("[Auto-SEO Failed]", err);
                        window.Modal.toast(window.locale.t('editor.ai_trigger_fail'), window.locale.t('common.warning') || "Warning");
                    });
                }
            }

        } catch (e) {
            console.error("Save Error", e);
            if (!silent) window.Modal.alert(window.locale.t('editor.save_failed', { e: e.message }), window.locale.t('common.error') || "Error");
        }
    }

    // [Helper] Upload Pending
    async uploadPendingImages() {
        // [Concurrency Lock] If already uploading, return the existing promise
        if (this.uploadPromise) {
            console.log("Joined existing upload process...");
            return this.uploadPromise;
        }

        // [FIX] Filter only purely pending, ignore 'uploading' or 'done'
        const pending = this.localFiles.filter(f => f.status === 'pending');
        if (!pending.length) return;

        // [LOCK] Mark all as uploading immediately to prevent race conditions
        pending.forEach(f => f.status = 'uploading');

        // Create a new promise for this batch
        this.uploadPromise = (async () => {
            try {
                // Parallel Limit? No, browser handles ~6. Let's do sequential or Promise.all.
                // Promise.all is faster but risky for memory? Webtoon images are small-ish if compressed.
                // Let's use sequential for safety/simplicity as implemented before.

                for (const item of pending) {
                    try {
                        const compressedBlob = await MediaService.compressImage(item.file, 0.8);

                        // [FIX] Use UUID-based Path (episodes/{uuid}) to prevent index collision
                        const epId = this.episode.id;
                        if (!epId) throw new Error("Episode ID missing for upload path");

                        const path = `series/${this.controller.seriesId}/episodes/${epId}/${Date.now()}_${item.id}.webp`;

                        const url = await MediaService.uploadImage(compressedBlob, path);

                        item.url = url;
                        item.status = 'done';
                        item.isLocal = false;
                    } catch (e) {
                        console.error("Upload failed for", item.name, e);
                        item.status = 'error';
                    }
                }
            } finally {
                // [Release Lock]
                this.uploadPromise = null;
            }
        })();

        return this.uploadPromise;
    }

    // [Validation Logic]
    validateImage(w, h, mode) {
        const ratio = h / w;

        if (mode === 'webtoon') {
            // Webtoon: Tall images (at least 1:1.2)
            if (ratio < 1.2) {
                return {
                    valid: false,
                    reason: window.locale.t('editor.val_webtoon_error', { ratio: ratio.toFixed(2) })
                };
            }
        } else if (mode === 'comic') {
            // Comics: Page like (2:3 ~ 3:4) OR Spread (W > H)
            const isPortraitPage = ratio >= 1.2 && ratio <= 1.6;
            const isSpread = ratio < 0.8;

            if (!isPortraitPage && !isSpread) {
                return {
                    valid: false,
                    reason: window.locale.t('editor.val_comic_error', { ratio: ratio.toFixed(2) })
                };
            }
        } else if (mode === 'insta') {
            // InstaToon: Square-ish (0.8 ~ 1.25)
            if (ratio < 0.8 || ratio > 1.25) {
                return {
                    valid: false,
                    reason: window.locale.t('editor.val_insta_error', { ratio: ratio.toFixed(2) })
                };
            }
        }
        return { valid: true };
    }
}

